import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  ArrowLeft, 
  ExternalLink, 
  Building2, 
  Tag, 
  History,
  TrendingUp,
  Heart,
  BarChart3
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import { Tool } from '../types';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/tools`;

const categoryMap = {
  text: { label: 'Text', class: 'text-bg' },
  image: { label: 'Image', class: 'image-bg' },
  video: { label: 'Video', class: 'video-bg' },
  code: { label: 'Code', class: 'code-bg' },
  other: { label: 'Other', class: 'other-bg' },
};

const getToolDesc = (tool: any, lang: string) => {
  if (!tool?.description) return '';
  if (typeof tool.description === 'string') return tool.description;
  const l = (lang || 'en').substring(0, 2);
  return tool.description[l] || tool.description.en || '';
};

const ToolDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tool, setTool] = useState<Tool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    const fetchTool = async () => {
      if (!id) return;
      try {
        const response = await axios.get(`${API_BASE}/${id}`);
        setTool(response.data);
        
        // Update SEO metadata
        document.title = `${response.data.name} - Reviews, Details and Access | AI Directory`;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', `Discover ${response.data.name}, an AI tool by ${response.data.company}. View its detailed description, pricing and statistics on AI Directory.`);
        }

        const savedFavs = JSON.parse(localStorage.getItem('fav_tools') || '[]');
        setIsFav(savedFavs.includes(id));
      } catch (err: any) {
        if (err.response && err.response.status === 404) {
          setError("Tool not found.");
        } else {
          setError("Error loading details.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTool();
  }, [id]);

  const toggleFavorite = () => {
    if (!id) return;
    const savedFavs = JSON.parse(localStorage.getItem('fav_tools') || '[]');
    let newFavs;
    if (savedFavs.includes(id)) {
      newFavs = savedFavs.filter((f: string) => f !== id);
      setIsFav(false);
    } else {
      newFavs = [...savedFavs, id];
      setIsFav(true);
    }
    localStorage.setItem('fav_tools', JSON.stringify(newFavs));
  };

  const trackClick = async () => {
    if (!id) return;
    try {
      await axios.post(`${API_BASE}/${id}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;
  if (error || !tool) return <div className="error-container"><p>{error || "Tool not found."}</p><button onClick={() => navigate('/')}>Back</button></div>;

  const cat = categoryMap[tool.category as keyof typeof categoryMap] || categoryMap.other;
  const totalClicks = tool.clickStats ? tool.clickStats.reduce((acc, curr) => acc + (curr.count || 0), 0) : 0;

  // Format data for Recharts
  const chartData = (tool.clickStats || [])
    .map(stat => ({
      date: new Date(stat.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
      clicks: stat.count
    }))
    .sort((a, b) => {
      const [dayA, monthA] = a.date.split('/').map(Number);
      const [dayB, monthB] = b.date.split('/').map(Number);
      return monthA !== monthB ? monthA - monthB : dayA - dayB;
    })
    // Take last 7 days for visibility
    .slice(-7);

  return (
    <div className="detail-page">
      <button className="back-btn" onClick={() => navigate(-1)}>
        <ArrowLeft size={20} /> {t('back_home', 'Back')}
      </button>

      <div className="detail-container">
        <div className="detail-header">
          <div className="title-section">
            <div className={`detail-category-badge ${cat.class}`}>{t(`categories.${tool.category}` as any, cat.label)}</div>
            <h1>{tool.name}</h1>
            <p className="company-name"><Building2 size={16} /> {t('created_by', 'Created by')} {tool.company}</p>
          </div>
          <button 
            className={`detail-fav-btn ${isFav ? 'active' : ''}`}
            onClick={toggleFavorite}
          >
            <Heart size={24} fill={isFav ? '#ef4444' : 'none'} />
          </button>
        </div>

        <div className="detail-content">
          <div className="main-info">
            <section>
              <h2>{t('tool.description')}</h2>
              <p>{getToolDesc(tool, i18n.language)}</p>
            </section>

            <div className="info-grid">
              <div className="info-item">
                <Tag size={20} />
                <div>
                  <small>{t('tool.pricing', 'Pricing')}</small>
                  <strong>{tool.pricing}</strong>
                </div>
              </div>
              <div className="info-item">
                <History size={20} />
                <div>
                  <small>{t('tool.version', 'Version')}</small>
                  <strong>{tool.version}</strong>
                </div>
              </div>
              <div className="info-item">
                <TrendingUp size={20} />
                <div>
                  <small>{t('total_visits', 'Total Visits')}</small>
                  <strong>{totalClicks}</strong>
                </div>
              </div>
            </div>

            <section className="chart-section">
              <div className="section-header">
                <BarChart3 size={20} />
                <h2>{t('activity_chart', 'Activity (Last 7 days)')}</h2>
              </div>
              <div className="chart-wrapper">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="date" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: '#64748b', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: '#64748b', fontSize: 12 }}
                      />
                      <Tooltip 
                        cursor={{ fill: 'transparent' }}
                        contentStyle={{ 
                          borderRadius: '8px', 
                          border: 'none', 
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' 
                        }}
                      />
                      <Bar dataKey="clicks" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === chartData.length - 1 ? '#6366f1' : '#a5b4fc'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="no-data">{t('no_stats', 'No statistics yet for this tool.')}</p>
                )}
              </div>
            </section>
          </div>

          <div className="action-section">
            <a 
              href={tool.url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="primary-btn"
              onClick={trackClick}
            >
              {t('tool.visit', 'Visit Official Website')} <ExternalLink size={20} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolDetail;
